/**
 * <SmartCell /> — cell renderer dispatcher.
 *
 * Receives a column meta and a raw value. Each branch is its own small
 * sub-component so a renderer can keep state (e.g. copy-to-clipboard
 * confirmation on `IdCell`).
 */

import React, { useState } from 'react';
import { Box, Tooltip, useTheme, alpha } from '@mui/material';
import {
  CheckRounded,
  CloseRounded,
  ContentCopyRounded,
  LaunchRounded,
} from '@mui/icons-material';
import type { ColumnMeta } from './types';
import {
  formatNumber,
  formatCurrency,
  formatPercent,
  formatDate,
  formatDateTime,
  relativeDate,
} from './format';
import { unwrapArrayLike } from './fieldTypes';
import { chipColorFor, getCategoryColor } from './palette';

// ─────────────────────────────────────────────────────────────────────────────
// Shared bits
// ─────────────────────────────────────────────────────────────────────────────

const NULL_GLYPH = '—';

// Every cell renderer extends this. `height: '100%'` is critical — DataGrid 8
// no longer auto-centers `renderCell` content vertically, so we own the
// alignment ourselves. Without it, fixed-height children (chips, icons)
// hug the top of the cell.
const cellBaseSx = {
  width: '100%',
  height: '100%',
  fontSize: '0.8125rem',
  lineHeight: 1.4,
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
} as const;

function NullCell({ align }: { align: 'left' | 'right' | 'center' }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        ...cellBaseSx,
        justifyContent:
          align === 'right' ? 'flex-end' :
          align === 'center' ? 'center' :
          'flex-start',
        color: theme.tokens.text.disabled,
        fontStyle: 'italic',
      }}
    >
      {NULL_GLYPH}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Number / Currency / Percent
// ─────────────────────────────────────────────────────────────────────────────

function NumericCell({
  value,
  meta,
}: {
  value: any;
  meta: ColumnMeta;
}) {
  const theme = useTheme();
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return <NullCell align={meta.align} />;
  }
  let text: string;
  if (meta.type === 'currency') {
    const compact = Math.abs(value) >= 10_000;
    text = formatCurrency(value, meta.currency, compact);
  } else if (meta.type === 'percent') {
    text = formatPercent(value, meta.percentScale);
  } else {
    text = formatNumber(value);
  }
  return (
    <Box
      sx={{
        ...cellBaseSx,
        justifyContent: 'flex-end',
        fontFamily: theme.typography.fontFamilyMono,
        fontVariantNumeric: 'tabular-nums',
        color: theme.tokens.text.primary,
        fontWeight: 500,
      }}
    >
      {text}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Date / DateTime
// ─────────────────────────────────────────────────────────────────────────────

function DateCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  if (value === null || value === undefined || value === '') {
    return <NullCell align={meta.align} />;
  }
  const text = meta.type === 'datetime' ? formatDateTime(value) : formatDate(value);
  const rel = relativeDate(value);
  return (
    <Tooltip title={rel || ''} arrow placement="top" disableHoverListener={!rel}>
      <Box sx={cellBaseSx}>{text}</Box>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Boolean
// ─────────────────────────────────────────────────────────────────────────────

function BooleanCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  const theme = useTheme();
  if (value === null || value === undefined) return <NullCell align={meta.align} />;
  const truthy = value === true || value === 1 || value === '1' || value === 'true';
  const falsy = value === false || value === 0 || value === '0' || value === 'false';
  if (truthy) {
    return (
      <Box sx={{ ...cellBaseSx, justifyContent: 'center', color: theme.tokens.semantic.success }}>
        <CheckRounded sx={{ fontSize: 18 }} />
      </Box>
    );
  }
  if (falsy) {
    return (
      <Box sx={{ ...cellBaseSx, justifyContent: 'center', color: theme.tokens.text.disabled }}>
        <CloseRounded sx={{ fontSize: 18 }} />
      </Box>
    );
  }
  return <Box sx={cellBaseSx}>{String(value)}</Box>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Chip / Multi-select
// ─────────────────────────────────────────────────────────────────────────────

function Chip({
  text,
  isDark,
  paletteIdx,
}: {
  text: string;
  isDark: boolean;
  paletteIdx?: number;
}) {
  const color = paletteIdx !== undefined
    ? getCategoryColor(paletteIdx, isDark)
    : chipColorFor(text, isDark);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        px: 0.875,
        borderRadius: '999px',
        fontSize: '0.6875rem',
        fontWeight: 600,
        lineHeight: 1,
        bgcolor: color.bg,
        color: color.fg,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
        letterSpacing: '0.01em',
      }}
    >
      {text}
    </Box>
  );
}

function CategoricalCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  if (value === null || value === undefined || value === '' || value === 'null') {
    return <NullCell align={meta.align} />;
  }
  const text = String(value);
  const idx = meta.categoryIndex?.get(text);
  return (
    <Box sx={cellBaseSx}>
      <Chip text={text} isDark={isDark} paletteIdx={idx} />
    </Box>
  );
}

function MultiSelectCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const raw = unwrapArrayLike(value);
  // Filter out any null/"null" items that slipped through unwrapping
  const arr = raw?.filter((item) => item !== null && item !== undefined && String(item) !== 'null') ?? null;
  if (!arr || arr.length === 0) {
    return <NullCell align={meta.align} />;
  }
  // Render up to 3 chips inline, then "+N" for the overflow.
  const visible = arr.slice(0, 3);
  const overflow = arr.length - visible.length;
  return (
    <Box sx={{ ...cellBaseSx, gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
      {visible.map((item, i) => {
        const text = typeof item === 'string' ? item : String(item);
        const idx = meta.categoryIndex?.get(text);
        return <Chip key={`${i}-${text}`} text={text} isDark={isDark} paletteIdx={idx} />;
      })}
      {overflow > 0 && (
        <Tooltip
          title={arr.slice(3).map((x) => String(x)).join(', ')}
          arrow
          placement="top"
        >
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 20,
              px: 0.75,
              borderRadius: '999px',
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: theme.tokens.text.tertiary,
              bgcolor: theme.tokens.surface.sunken,
              border: `1px solid ${theme.tokens.border.subtle}`,
            }}
          >
            +{overflow}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Id (record ids, uuids, hashes)
// ─────────────────────────────────────────────────────────────────────────────

function IdCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  if (value === null || value === undefined || value === '') {
    return <NullCell align={meta.align} />;
  }
  const text = String(value);
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // ignore
    }
  };
  return (
    <Tooltip title={copied ? 'Copied!' : text} arrow placement="top">
      <Box
        onClick={handleCopy}
        sx={{
          ...cellBaseSx,
          fontFamily: theme.typography.fontFamilyMono,
          color: theme.tokens.text.tertiary,
          fontSize: '0.75rem',
          cursor: 'pointer',
          gap: 0.5,
          '&:hover .copy-glyph': { opacity: 1 },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {text}
        </Box>
        {copied ? (
          <CheckRounded
            className="copy-glyph"
            sx={{ fontSize: 12, color: theme.tokens.semantic.success, opacity: 1 }}
          />
        ) : (
          <ContentCopyRounded
            className="copy-glyph"
            sx={{ fontSize: 12, opacity: 0, transition: 'opacity 120ms' }}
          />
        )}
      </Box>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Email / URL
// ─────────────────────────────────────────────────────────────────────────────

function EmailCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  const theme = useTheme();
  if (value === null || value === undefined || value === '') {
    return <NullCell align={meta.align} />;
  }
  const text = String(value);
  return (
    <Tooltip title={`Send mail to ${text}`} arrow placement="top">
      <Box
        component="a"
        href={`mailto:${text}`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        sx={{
          ...cellBaseSx,
          color: theme.tokens.brand.primary,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <Box
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {text}
        </Box>
      </Box>
    </Tooltip>
  );
}

function UrlCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  const theme = useTheme();
  if (value === null || value === undefined || value === '') {
    return <NullCell align={meta.align} />;
  }
  const text = String(value);
  let label = text;
  try {
    const u = new URL(text);
    label = u.hostname.replace(/^www\./, '') + (u.pathname !== '/' ? u.pathname : '');
  } catch {
    // not parseable, use raw
  }
  return (
    <Tooltip title={text} arrow placement="top">
      <Box
        component="a"
        href={text}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        sx={{
          ...cellBaseSx,
          color: theme.tokens.brand.primary,
          textDecoration: 'none',
          gap: 0.5,
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        <Box
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {label}
        </Box>
        <LaunchRounded sx={{ fontSize: 12, flexShrink: 0 }} />
      </Box>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// JSON / object
// ─────────────────────────────────────────────────────────────────────────────

function JsonCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  const theme = useTheme();
  if (value === null || value === undefined) return <NullCell align={meta.align} />;
  let preview = '';
  let full = '';
  try {
    if (typeof value === 'string') {
      const parsed = JSON.parse(value);
      preview = Array.isArray(parsed) ? `[${parsed.length}]` : '{…}';
      full = JSON.stringify(parsed, null, 2);
    } else {
      preview = Array.isArray(value) ? `[${value.length}]` : '{…}';
      full = JSON.stringify(value, null, 2);
    }
  } catch {
    preview = '{…}';
    full = String(value);
  }
  return (
    <Box sx={cellBaseSx}>
      <Tooltip
        title={
          <Box
            component="pre"
            sx={{ m: 0, fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap', maxWidth: 400 }}
          >
            {full.slice(0, 1000)}
            {full.length > 1000 ? '…' : ''}
          </Box>
        }
        arrow
        placement="top"
      >
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: theme.tokens.text.tertiary,
            fontFamily: theme.typography.fontFamilyMono,
            fontSize: '0.6875rem',
            height: 20,
            px: 0.875,
            borderRadius: '4px',
            bgcolor: theme.tokens.surface.sunken,
            border: `1px solid ${theme.tokens.border.subtle}`,
            maxWidth: '100%',
            whiteSpace: 'nowrap',
          }}
        >
          {preview}
        </Box>
      </Tooltip>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Long text (multi-line clamp)
// ─────────────────────────────────────────────────────────────────────────────

function LongTextCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  const theme = useTheme();
  if (value === null || value === undefined || value === '') {
    return <NullCell align={meta.align} />;
  }
  const text = String(value);
  return (
    <Tooltip title={text.length > 200 ? text.slice(0, 600) + (text.length > 600 ? '…' : '') : ''} arrow placement="top" disableHoverListener={text.length <= 200}>
      <Box
        sx={{
          ...cellBaseSx,
          color: theme.tokens.text.primary,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal',
          alignItems: 'flex-start',
          py: 0.5,
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Plain text
// ─────────────────────────────────────────────────────────────────────────────

function TextCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  const theme = useTheme();
  if (value === null || value === undefined || value === '') {
    return <NullCell align={meta.align} />;
  }
  const text = String(value);
  const truncated = text.length > 80;
  return (
    <Tooltip title={truncated ? text : ''} arrow placement="top" disableHoverListener={!truncated}>
      <Box
        sx={{
          ...cellBaseSx,
          color: theme.tokens.text.primary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dispatcher
// ─────────────────────────────────────────────────────────────────────────────

export function SmartCell({ value, meta }: { value: any; meta: ColumnMeta }) {
  // Treat null-like strings as SQL NULL. dlt can store Python None as the
  // literal string "null", and Airtable linked-record fields come as JSON
  // arrays — an empty linked record produces "[null]". Both should render
  // as the dash glyph, not as a chip or plain text.
  if (
    value === null ||
    value === undefined ||
    value === 'null' ||
    value === '[null]'
  ) return <NullCell align={meta.align} />;

  switch (meta.type) {
    case 'number':
    case 'currency':
    case 'percent':
      return <NumericCell value={value} meta={meta} />;
    case 'date':
    case 'datetime':
      return <DateCell value={value} meta={meta} />;
    case 'boolean':
      return <BooleanCell value={value} meta={meta} />;
    case 'categorical':
      return <CategoricalCell value={value} meta={meta} />;
    case 'multi_select':
      return <MultiSelectCell value={value} meta={meta} />;
    case 'id':
      return <IdCell value={value} meta={meta} />;
    case 'email':
      return <EmailCell value={value} meta={meta} />;
    case 'url':
      return <UrlCell value={value} meta={meta} />;
    case 'json':
      return <JsonCell value={value} meta={meta} />;
    case 'long_text':
      return <LongTextCell value={value} meta={meta} />;
    case 'null':
      return <NullCell align={meta.align} />;
    case 'text':
    default:
      return <TextCell value={value} meta={meta} />;
  }
}

// Suppress unused — kept exported for potential consumers that want subtle
// spacing tweaks around a cell on outline frames.
void alpha;
